import type {
  PieChartComponentSpec,
  PieSortOption,
  ProcessedChartData,
  WidgetResponseSpec,
} from "./types";
import { STYLE_PRESETS } from "./presets";
import { formatSeriesLabel } from "./seriesLabel";
import { formatToDecimals, inferDecimalsFromLabels } from "./decimals";

interface PieSlice {
  name: string;
  y: number;
  color?: string;
  /** The value formatted to the metric's decimal count, for the label and tooltip. */
  custom: { valueLabel: string };
}

function aggregateValues(values: number[], agg: "avg" | "sum" | "best"): number {
  if (values.length === 0) return 0;
  if (agg === "best") return Math.max(...values);
  if (agg === "avg") return values.reduce((a, b) => a + b, 0) / values.length;
  return values.reduce((a, b) => a + b, 0);
}

function sortSlices(slices: PieSlice[], sortBy: PieSortOption): PieSlice[] {
  return [...slices].sort((a, b) => {
    switch (sortBy) {
      case "value-desc": return b.y - a.y;
      case "value-asc":  return a.y - b.y;
      case "alpha-asc":  return a.name.localeCompare(b.name);
      case "alpha-desc": return b.name.localeCompare(a.name);
      default:           return 0;
    }
  });
}

export function buildPieConfig(
  spec: PieChartComponentSpec,
  response: WidgetResponseSpec,
): ProcessedChartData {
  const donutMode      = spec.widgetMutations.donutMode ?? false;
  const sortConfig     = spec.widgetMutations.sortConfig;
  const firstSeries    = spec.series[0];
  const showDataLabels = firstSeries?.plotMutations.showDataLabels ?? false;
  const showPercentages = firstSeries?.plotMutations.showPercentages ?? false;
  const showLegend     = spec.display.showLegend;

  const palette = STYLE_PRESETS.default.colors;

  // Each series entry is one slice — find its response series and aggregate
  // across data points (backend returns date-keyed rows when groupby: []).
  let slices: PieSlice[] = spec.series.map((s, i) => {
    const seriesData = response.series.find((r) => r.query_id === s.queryId);
    const points = seriesData?.data ?? [];
    // A null value is an empty bucket; a pie slice aggregates only real values.
    const values = points
      .map((d) => d.value)
      .filter((v): v is number => v != null);
    const y = aggregateValues(values, s.metricAgg);
    // The backend labels each point to the metric's decimal count. The slice
    // aggregates the values here, so it formats the result to the same count.
    const decimals = inferDecimalsFromLabels(points.map((d) => d.value_label)) ?? 2;
    const misc = s.series_builder_misc;
    const override = s.displayNameOverride?.trim() || misc.labelOverride?.trim();
    const name = override || formatSeriesLabel(s.displayName, s.metricAgg, s.metricIdentifierType, misc);
    return {
      name,
      y,
      color: s.colorOverride || palette[i % palette.length],
      custom: { valueLabel: formatToDecimals(y, decimals) },
    };
  }).filter((s) => s.y > 0);

  if (sortConfig?.enabled) {
    slices = sortSlices(slices, sortConfig.sortBy);
  }

  const chartOptions = {
    colors: palette,
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      margin: [showLegend ? 50 : 4, 4, 4, 4],
    },
    title:  { text: "" },
    credits: { enabled: false },
    tooltip: {
      pointFormat: "<b>{point.name}</b>: <b>{point.custom.valueLabel}</b> ({point.percentage:.1f}%)",
    },
    legend: {
      enabled: showLegend,
      align: "center",
      verticalAlign: "top",
      layout: "horizontal",
      itemStyle: { fontSize: "11px", fontWeight: "400", color: "#1a1a1a" },
      maxHeight: 80,
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        innerSize: donutMode ? "50%" : "0%",
        showInLegend: showLegend,
        dataLabels: {
          enabled: showDataLabels || showPercentages,
          format: showDataLabels && showPercentages
            ? "<b>{point.name}</b>: {point.custom.valueLabel} ({point.percentage:.1f}%)"
            : showPercentages
              ? "<b>{point.name}</b>: {point.percentage:.1f}%"
              : "<b>{point.name}</b>: {point.custom.valueLabel}",
          style: { fontSize: "11px", fontWeight: "400", textOutline: "none" },
        },
      },
    },
    series: [
      {
        type: "pie",
        name: firstSeries?.displayNameOverride?.trim() || firstSeries?.displayName || "Value",
        data: slices,
      },
    ],
    exporting: { enabled: false },
  };

  return { chartOptions };
}
